import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const RoboticProcessAutomation = () => {
  const phrases = [
    "Unattended Automation",
    "Cognitive Surface Scraping",
    "Legacy Integration Matrix"
  ];

  const cardData = [
    { type: 'steel-core', content: '🤖' },
    { type: 'emerald-wave', content: '⚡' },
    { type: 'terminal-stack', content: '⚙️' },
    { type: 'ocr-spark', content: '🔍' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "🤖", title: "Unattended Core Bot Infrastructure", desc: "Deploy independent background software executors designed to orchestrate high-volume data entry, transaction matching, and statement generation without manual intervention." },
    { icon: "👁️", title: "AI-Powered Cognitive Surface Vision", desc: "Integrate computer vision scraping algorithms capable of recognizing dynamically changing application controls, Citrix environments, and remote desktops." },
    { icon: "🔌", title: "Non-Invasive Legacy Unification", desc: "Bridge communication gaps between older mainframe terminals and web applications without modifying brittle, decades-old source codebases." },
    { icon: "📄", title: "Intelligent Document Parsing & OCR", desc: "Extract unstructured business tables, purchase invoices, and physical legal forms cleanly into highly normalized structured database payloads." },
    { icon: "📊", title: "Central Orchestrator Cockpits", desc: "Control, assign licenses to, and instantly audit thousands of active software runtimes from a unified control tower management screen." },
    { icon: "⚙️", title: "Exception Routing & Human-in-the-Loop", desc: "Construct fault-tolerant automated exception flows that automatically isolate data anomalies for manual review before continuing runs." },
    { icon: "🛡️", title: "Airtight Bot Governance & Credential Hubs", desc: "Isolate software identity permissions using encrypted enterprise credential vaults, ensuring full regulatory security compliance." }
  ];

  const advancedServices = [
    { icon: "⏳", title: "Dynamic SLA Workload Distributing", desc: "Program orchestrator nodes to automatically spin up extra virtual machine bots when transaction backlogs exceed target parameters." },
    { icon: "🔍", title: "Automated System Process Mining", desc: "Run continuous algorithmic analytical background tracking to map employee desktop clicks and flag ideal processes for RPA migration." },
    { icon: "📨", title: "Cognitive Email Triage Matrix", desc: "Parse inbound business mail intent, classify attached documentation, and automatically route jobs to appropriate back-office bot paths." },
    { icon: "⚖️", title: "Transactional Auditing Records", desc: "Generate unalterable log footprints detailing exact action sequences executed by bots to pass financial compliance checkmarks cleanly." },
    { icon: "🧪", title: "Shadow Execution Load Profiling", desc: "Execute modified automated workflow models in parallel with active scripts to record variance errors prior to live system replacement." },
    { icon: "💼", title: "Cross-App Multi-Step Reconciliation", desc: "Synchronize banking deposits, internal ERP accounting ledger lines, and third-party portal exports down to zero manual oversight." },
    { icon: "🚨", title: "UI Break-Prevention Triggers", desc: "Embed fallback script layers that automatically pivot to native direct API tracking routes if main software surfaces update layouts." },
    { icon: "🗃️", title: "Structured Web Form Scalers", desc: "Automate heavy citizen registration uploads, permit filings, and high-frequency supplier portal data populations cleanly." },
    { icon: "🛠️", title: "Desktop Attended Sidekick Assistants", desc: "Provide field customer service employees with context-aware micro-bots that fetch legacy data values on hotkey demand." }
  ];

  const technologies = {
    "RPA Core Platforms": ["UiPath Enterprise Platform", "Automation Anywhere A360", "Microsoft Power Automate Desktop", "Blue Prism Cloud Architectures"],
    "Cognitive & Vision Engines": ["Google Cloud Vision API", "ABBYY FineReader OCR Engine", "Tesseract Text Extraction Tools", "AWS Textract Intelligent Parsing"],
    "Infrastructure & Management": ["UiPath Orchestrator Hub", "Docker Virtualization Systems", "Windows Server Active Directory", "CyberArk Credential Vaults"],
    "Integration & Fallbacks": ["Python App Interfacing SDKs", "Win32 COM Automation Links", "Selenium Web Driver Frameworks", "Node.js Desktop Automation Tools"],
    "Analytics & Optimization": ["Celonis Process Mining Blocks", "UiPath Insights Dashboards", "Tableau Activity Monitoring Panels", "Splunk Log Collection Systems"]
  };

  const processSteps = [
    { num: "01", title: "Process Discovery & Mining", desc: "Auditing internal employee click steps, quantifying operational velocity, and scoring optimization complexity boundaries." },
    { num: "02", title: "Process Definition Blueprinting", desc: "Drafting micro-step keystroke maps, isolating transaction variables, and mapping explicit exception boundaries." },
    { num: "03", title: "Automation Script Engineering", desc: "Programming logical rule passes, surface scraping hooks, and credential handling methods." },
    { num: "04", title: "Orchestrator Sandbox Integration", desc: "Configuring runtime allocation boundaries and deploying test triggers inside isolated sandbox nodes." },
    { num: "05", title: "Stress Straining & Security Audits", desc: "Executing high-volume loop passes, handling fake data errors, and certifying encryption compliance parameters." },
    { num: "06", title: "Live Unattended Deployment", desc: "Provisioning active digital workers on server instances to begin autonomous target system processing." },
    { num: "07", title: "Operational Variance Tuning", desc: "Reviewing execution logs, refining selector rules against target system changes, and scaling compute blocks." }
  ];

  const industries = [
    "Banking, Loan & Mortgage Processing", "Insurance Claim & Underwriting Review", "Healthcare Patient Enrollment & Billing",
    "Global Supply Chain & Customs Filings", "Corporate Accounts Payable & Invoice Triage", "Telecom Subscription Provisioning Hubs",
    "Human Resources Employee Onboarding", "Retail Inventory Adjustment Reporting", "Energy Utility Billing & Meter Auditing",
    "Public Sector Licensing Administration", "Legal Contract Metadata Classification", "Hospitality Group Booking Synchronization"
  ];

  const designBenefits = [
    "Zero Human Typographical Processing Errors", "Up to 80% Drop in Average Cycle Durations", "Continuous 24/7/365 Runtime Availability",
    "Non-Invasive Integration with Legacy Infrastructure", "Massive Savings in Back-Office Capital Allocations", "Rapid Implementation to Production Timelines",
    "Total Structural Scaling Elasticity Options", "100% Comprehensive Digital Audit Footprints"
  ];

  const whyChooseUs = [
    "Certified Enterprise RPA Automation Engineers", "Proven Real-World Process Migration Footprints", "Airtight Zero-Trust Credential Hardening",
    "Deep UI Surface Object Identification Skills", "Platform-Agnostic Infrastructure Architecture Setups", "Comprehensive Exception Mitigation Frameworks",
    "End-to-End Orchestrator Management Ownership", "Transparent ROI Validation Methodologies", "High-Availability Digital Worker Maintenance Support",
    "Experienced Legacy Mainframe System Integrators", "Iterative Sprint-Based Flow Prototyping", "100% Core Transactional Stability Commitments"
  ];

  return (
    <>
      <Helmet>
        {/* Primary SEO */}
        <title>
          Robotic Process Automation (RPA) Services | Intelligent Automation Solutions | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Robotic Process Automation (RPA) Services | Intelligent Automation Solutions | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. offers Robotic Process Automation (RPA) services, including intelligent automation, workflow automation, UiPath, Automation Anywhere, Blue Prism, business process automation, AI-powered automation, and enterprise RPA solutions."
        />

        <meta
          name="keywords"
          content="Robotic Process Automation, RPA Services, RPA Development, Intelligent Automation, Business Process Automation, Workflow Automation, UiPath Development, Automation Anywhere, Blue Prism, AI Automation, Hyperautomation, Enterprise Automation, Digital Process Automation, Robotic Automation, Process Optimization, RPA Consulting, Automation Solutions, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/robotic-process-automation"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Robotic Process Automation (RPA) Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Automate repetitive business processes with Robotic Process Automation services from Affsoft IT Solutions Pvt. Ltd. We specialize in UiPath, Automation Anywhere, Blue Prism, workflow automation, and intelligent enterprise automation."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/robotic-process-automation"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Robotic Process Automation (RPA) Services | Intelligent Automation Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Increase efficiency with Robotic Process Automation services from Affsoft IT Solutions Pvt. Ltd. We deliver intelligent automation, workflow automation, UiPath, Blue Prism, Automation Anywhere, and enterprise RPA solutions."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="rpa-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0f172a' // Deep slate/industrial fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Slow-Scrolling Marquee Text Background Wrapper */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Robotic &nbsp; Process &nbsp; Automation &nbsp; &amp; &nbsp;</span>
              <span>Digital Worker Frameworks </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-emerald fs-3" style={{ color: '#10b981' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Robotic Process Automation</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we eliminate the manual friction dragging down corporate back-offices. Our robotic process automation engineering builds smart digital workers that handle high-volume data entries, navigate legacy terminal screens, and bridge system silos—boosting execution speeds while keeping operational error rates at absolute zero.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Map an Automation Flow</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#10b981' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#10b981' }}>Digital Workforce Engineering</span>
              <h2 className="fw-bold text-dark display-6">Automate Routine Desktop Tasks Across Fragmented Legacy Applications</h2>
              <p className="text-muted mt-3">
                Slow manual data transcription, broken copy-paste routines, and high operational transaction backlogs lead to administrative errors and costly operational leaks. At Affsoft IT Solutions Pvt. Ltd., we construct robust, secure, and fully auditable digital workflows built for scale.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#10b981' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Strategic Enterprise RPA Deployment</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#10b981' }}>Intelligent Process Unification</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Automation Capabilities Crafted for Enterprise Elasticity</h2>
              <p className="text-muted mt-3">
                We upgrade traditional linear macros into highly resilient cognitive automation matrices. From managing elastic server runtime allocations to scanning unstructured paperwork, our codebases safeguard your operational accuracy.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#10b981' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#34d399' }}>Automation Architecture Toolsets</span>
              <h2 className="fw-bold display-6">RPA Core Environments & Unification Tools We Support</h2>
              <p className="text-light-50">We structure, coordinate, and monitor virtual worker platforms using trusted global core systems.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#34d399' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#34d399' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Automation Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered RPA Implementation Framework</h2>
              <p className="text-muted">A clear, highly disciplined delivery roadmap designed to transition manual operations cleanly onto automated, high-speed software runs.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#10b981' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#10b981' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Automate</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine precise surface target selectors with modern software control logging to guarantee reproducible digital worker outcomes.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#10b981' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default RoboticProcessAutomation;